import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { useChatHistory, useChatActions, useFileUpload } from "./hooks";
import {
  ChatHeader,
  ChatArea,
  ChatInput,
  MessageStatus
} from "./components/chat";
import { ChatSidebar } from "./components/sidebar";
import config from "./config";
import { adminTheme, adminColors } from "./theme/adminTheme";
import { authFetch, handleApiResponse, describeApiError } from "./api/authFetch";

function Chat() {
  const { conversationId: conversationIdParam } = useParams();
  const conversationId = Number(conversationIdParam);
  const routerNavigate = useNavigate();
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUsername, setCurrentUsername] = useState(null);
  // Sidebar renders as a persistent column at md+ and as an overlay Drawer
  // below md (see ChatSidebar.js) — this only tracks the drawer's open state.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Custom hooks for chat functionality
  const {
    chatHistory,
    setChatHistory,
    message,
    messageType,
    chatBoxRef,
    scrollToBottom,
    isNearBottom,
    jumpToLatest,
    showMessage,
    clearChatHistory,
    navigate
  } = useChatHistory({ conversationId });

  // File upload functionality
  const {
    stagedFiles,
    uploadStagedFiles,
    removeStagedFile,
    clearStagedFiles,
    handleFileUpload,
    handleFileDelete,
    handleFileDownload,
    getStagedFilePreviewUrl
  } = useFileUpload({ showMessage });

  const {
    input,
    setInput,
    isSearching,
    editingMessageId,
    editingContent,
    setEditingContent,
    sendMessage,
    stopGenerating,
    startEditMessage,
    cancelEditMessage,
    saveEditedMessage,
    deleteMessage,
    handleLogout
  } = useChatActions({
    chatHistory,
    setChatHistory,
    navigate,
    showMessage,
    uploadStagedFiles,
    clearStagedFiles,
    stagedFiles,
    scrollToBottom,
    conversationId
  });

  // Sidebar thread selection: a real id navigates to that thread, null
  // (e.g. after deleting the active thread) falls back to /chat, which
  // resolves to the most recent remaining thread or creates a new one.
  const handleSelectThread = (threadId) => {
    routerNavigate(threadId ? `/chat/${threadId}` : "/chat");
  };

  // Fetch current user role. Runs once on mount only (empty deps) - navigate
  // and showMessage are captured via closure rather than listed as
  // dependencies, since showMessage is recreated every render and would
  // otherwise re-trigger this fetch on every state update.
  useEffect(() => {
    let cancelled = false;
    const fetchUserProfile = async () => {
      try {
        const response = await authFetch(`${config.API_BASE_URL}/api/me`);
        const userData = await handleApiResponse(response, navigate);
        if (userData === null) return; // redirected to signin (401)
        if (!cancelled) {
          setCurrentUserRole(userData.role);
          setCurrentUsername(userData.username);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        if (!cancelled) {
          showMessage(`We couldn't load your account info: ${describeApiError(error)}`, "error");
        }
      }
    };

    fetchUserProfile();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle a tap on one of ChatArea's empty-state starter-prompt cards.
  //
  // sendMessage() (from useChatActions) takes an explicit text-override
  // parameter, so this calls it synchronously with promptText instead of
  // staging it into `input` via setInput() and deferring the actual send by
  // a tick (the old approach silently failed in production - see the
  // starter-prompt cards bug). The isSearching check below guards against a
  // double-tap; sendMessage's own internal (!currentInput || isSearching)
  // guard is the authoritative one and returns false if it bails (e.g. a
  // second tap that lands before this render sees isSearching flip), in
  // which case we fall back to putting the prompt text in the composer so
  // the student can still send it manually.
  const handleStarterPrompt = (promptText) => {
    if (isSearching) return;
    sendMessage({ preventDefault() {} }, promptText).then((dispatched) => {
      if (!dispatched) {
        setInput(promptText);
      }
    });
  };

  // Handle file staging (no immediate chat message)
  const handleFileStagingWithChat = async (file, settings) => {
    try {
      await handleFileUpload(file, { stage: true, ...settings });
      showMessage(`File "${file.name}" staged successfully`, "success");
    } catch (error) {
      console.error('Error staging file:', error);
      showMessage(`Failed to stage file: ${describeApiError(error)}`, "error");
    }
  };

  // Handle removing staged files
  const handleRemoveStagedFile = (fileId) => {
    removeStagedFile(fileId);
  };

  // Handle previewing staged files.
  // window.open() with no URL returns null when the browser's popup blocker
  // steps in (common on the first attempt in a fresh tab) rather than
  // throwing - calling .document on that would crash the whole chat view,
  // not just fail the preview. The object URL itself is created/cached by
  // useFileUpload's getStagedFilePreviewUrl and revoked when this staged
  // file is removed or the batch is cleared, so a blocked popup here still
  // leaves it in a state that gets cleaned up normally.
  const handlePreviewStagedFile = (file) => {
    if (!file.type?.startsWith('image/')) {
      showMessage("Preview not available for this file type", "error");
      return;
    }
    const url = getStagedFilePreviewUrl(file);
    const newWindow = window.open();
    if (!newWindow) {
      showMessage("Your browser blocked the preview popup. Please allow popups for this site and try again.", "error");
      return;
    }
    newWindow.document.write(`<img src="${url}" style="max-width: 100%; max-height: 100%;">`);
  };

  // Handle file deletion from messages.
  // handleFileDelete (useFileUpload.js) already shows its own success/error
  // message and never rethrows, so it fully owns user feedback here - adding
  // another message on top would either duplicate the success message or,
  // worse, show a false "deleted successfully" after a failure.
  const handleMessageFileDelete = async (file) => {
    await handleFileDelete(file);
  };

  return (
    <Box
      sx={{
        // dvh reflects the *visible* viewport on mobile browsers (excludes the
        // collapsing URL/toolbar chrome); vh does not, so it can leave the
        // composer clipped off-screen. Keep vh as the fallback for browsers
        // without dvh support.
        height: "100vh",
        "@supports (height: 100dvh)": {
          height: "100dvh",
        },
        overflow: "hidden",
        display: "flex",
        flexDirection: "row",
        backgroundColor: adminColors.surface,
        fontFamily: adminTheme.typography.fontFamily
      }}
    >
      {/* Chat thread sidebar — persistent column at md+, overlay Drawer below md */}
      <ChatSidebar
        activeConversationId={conversationId}
        onSelectThread={handleSelectThread}
        showMessage={showMessage}
        username={currentUsername}
        userRole={currentUserRole}
        onLogout={handleLogout}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Chat Header */}
        <ChatHeader
          onLogout={handleLogout}
          onClearChat={clearChatHistory}
          onOpenSidebar={() => setMobileSidebarOpen(true)}
        />

        {/* Main chat container */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            position: "relative",
            height: 0, // Force flex item to respect height constraints
            minHeight: 0 // Ensure flex item can shrink
          }}
        >
          {/* Chat Area - keyed on the thread id so switching threads never
              shows a flash of the previous thread's messages mid-fetch */}
          <ChatArea
            key={conversationId}
            chatBoxRef={chatBoxRef}
            chatHistory={chatHistory}
            editingMessageId={editingMessageId}
            editingContent={editingContent}
            setEditingContent={setEditingContent}
            onStartEdit={startEditMessage}
            onSaveEdit={saveEditedMessage}
            onCancelEdit={cancelEditMessage}
            onDelete={deleteMessage}
            onFileDelete={handleMessageFileDelete}
            onFileDownload={handleFileDownload}
            onStarterPrompt={handleStarterPrompt}
            isNearBottom={isNearBottom}
            onJumpToLatest={jumpToLatest}
          />

          {/* Chat Input - Fixed at bottom */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: adminColors.surfaceRaised,
              borderTop: "1px solid rgba(24,24,27,0.08)",
              display: "flex",
              justifyContent: "center",
              pt: 2,
              // env(safe-area-inset-bottom) clears the iOS home indicator on
              // notched devices; calc() falls back to plain 16px elsewhere.
              pb: "calc(16px + env(safe-area-inset-bottom, 0px))"
            }}
          >
            <Box
              sx={{
                maxWidth: "768px",
                width: "100%",
                px: { xs: 2, sm: 3 }
              }}
            >
              <ChatInput
                input={input}
                setInput={setInput}
                isSearching={isSearching}
                onSubmit={sendMessage}
                onStop={stopGenerating}
                onFileUpload={handleFileStagingWithChat}
                stagedFiles={stagedFiles}
                onRemoveStagedFile={handleRemoveStagedFile}
                onPreviewStagedFile={handlePreviewStagedFile}
                showMessage={showMessage}
              />
              {/* Message Status */}
              <MessageStatus message={message} messageType={messageType} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Chat;
